import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { ContractDetail } from "@ServerTypes/contracts/contracts.schema";

/**
 * State stored under the `contractDetails` redux key. It is the loaded `ContractDetail`
 * server entity returned by `getContracts` (`/contractsController`'s `result[0]`) — both
 * dispatch sites set a full `ContractDetail` — augmented with extra fields the aggregation
 * projects that the base schema does not model. The server-required `ContractDetail` fields
 * (`_id`, `versionId`, `type`, `lockData`, …) are therefore NON-optional here, so consumers
 * read them WITHOUT `!`; the empty initial state is an asserted pre-load placeholder. Only
 * the aggregation extras below are optional (genuinely not always present).
 */
export type ContractDetailsState = ContractDetail & {
  budgetAmount?: number;
  remainingBudgetAmountForMileStones?: number;
  PO_NO?: string;
  allParents?: string[];
  allChilds?: string[];
  isAnyOneChildContractLocked?: boolean;
  organisationData?: { name?: string };
  updatedBy?: { _id?: string; fullName?: string; firstName?: string; lastName?: string; profilePic?: string | null };
  // `role` is the numeric folderRoles id, matching `contractUserSchema` in
  // server/contracts/contracts.collection.ts (`role: z.number().optional()`) — it was declared
  // `string` here, which forced a cast wherever the roster reached ModuleUsersAccess.
  users?: Array<{ id: string; role: number; permissions: number[] }>;
};

/**
 * The cleared/pre-load placeholder for the slice. Because `ContractDetailsState` deliberately
 * declares the server-required `ContractDetail` fields non-optional (see above) so the read
 * sites need no `!`, there is no honest value to hold before `getContractDetails` dispatches
 * one — `{}` is that hole. The assertion is confined to this single constant rather than
 * repeated at every place the empty state is produced.
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- documented pre-load placeholder for a slice typed as always-loaded; nothing exists yet to validate.
const EMPTY_CONTRACT_DETAILS = {} as ContractDetailsState;

const contractSlice = createSlice({
  name: "contractDetails",
  initialState: EMPTY_CONTRACT_DETAILS,
  reducers: {
    setContractDetails: (_state: ContractDetailsState, action: PayloadAction<ContractDetailsState | undefined>) =>
      // An undefined payload clears the slice back to the pre-load placeholder. Spread so each
      // dispatch yields a fresh object, as redux expects of a state replacement.
      ({ ...(action.payload ?? EMPTY_CONTRACT_DETAILS) }),
  },
});

const { setContractDetails: _setContractDetailsRaw } = contractSlice.actions;

/**
 * Object-payload action creator wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 *
 * `Exact<>` is applied to the PARAMETER (`T & Exact<…>`) rather than used as the constraint — the
 * form the helper's own docs use. Under `exactOptionalPropertyTypes` type-fest's `ExactObject`
 * homomorphic mapping re-emits every optional member as `k?: V | undefined`, so a bare
 * `T extends Exact<ContractDetailsState | undefined, T>` no longer proves `T` is a
 * `ContractDetailsState`. Constraining `T` to the state type and intersecting `Exact<>` into the
 * parameter keeps both properties: the argument is still assignable to the raw action creator, and
 * excess keys are still mapped to `never` and rejected.
 */
export const setContractDetails = <T extends ContractDetailsState | undefined>(
  payload: T & Exact<ContractDetailsState | undefined, T>,
) => _setContractDetailsRaw(payload);

export const contractDetails = contractSlice.reducer;

export default contractSlice.reducer;
